import {COMPANY_KEYS} from "@/api/companyApi";
import Modal from "@/components/Common/Modal";
import {useLogout} from "@/hooks/useLogout";
import {useUserId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import AvatarPlaceholder from "@/images/navbar_logged_out_profile_icon.svg";
import {queryClient} from "@/queryClient";
import type {TAccountMember} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";

const RemoveCompanyMemberModal: FC<{
  companyId: number;
  companyName: string;
  companyMember: TAccountMember;
  onClose: () => void;
}> = ({companyId, companyName, companyMember, onClose}) => {
  const {t} = useTranslation("multi_account");
  const logout = useLogout();
  const loggedInUserId = useUserId();
  const isRemovingSelf = useMemo(
    () => companyMember.user.id === loggedInUserId,
    [companyMember.user.id, loggedInUserId],
  );

  const {mutate: removeMember} = useMutation({
    mutationKey: COMPANY_KEYS.mCompanyMemberRemoval(companyId),
    mutationFn: () => client.post(COMPANY_KEYS.mCompanyMemberRemoval(companyId)[0], {user_id: companyMember.user.id}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyMembers(companyId)});

      if (isRemovingSelf) {
        logout();
      }
    },
  });

  return (
    <Modal
      backgroundClassName="max-w-[600px]"
      modalTitle={t(isRemovingSelf ? "leave_company" : "remove_user_from_account")}
      onClose={onClose}
      onConfirm={removeMember}
      confirmButtonText={t(isRemovingSelf ? "leave_company" : "remove_user")}>
      <div className="flex flex-col items-center gap-5">
        {!isRemovingSelf && <span className="text-neutral-black">{t("remove_user")}</span>}
        <div className="border-neutral-grey-light-dark flex items-center gap-2 rounded-full border p-1 pr-2">
          <div className="size-6 shrink-0 rounded-full">
            <img
              src={companyMember?.user.profile_picture ?? AvatarPlaceholder}
              className="h-full w-full rounded-full object-fill"
            />
          </div>
          <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
            {companyMember?.user.first_name} {companyMember?.user.last_name}
          </span>
        </div>
        <span className="text-neutral-black">
          {t(isRemovingSelf ? "leave_company_confirmation" : "from_company", {
            companyName: companyName,
          })}
        </span>
        <div className="bg-warning-light-yellow text-warning-dark-yellow self-stretch rounded-md px-4 py-5 text-center text-lg font-semibold">
          {t(isRemovingSelf ? "leave_company_warning" : "remove_user_warning")}
        </div>
      </div>
    </Modal>
  );
};

export default RemoveCompanyMemberModal;
